<script setup lang="ts">
import { formatMegawatts, formatDateUSCentral, formatDateUSCentralShort } from '@/util/formatters'

interface ILoadForecastData {
  iso: string
  timestamp: string
  issue_date: string
  load_resource_zone: string
  expected_load_mwh: number
  actual_load_mwh: number
  is_peak_hour: boolean
}

const props = defineProps<{ data: ILoadForecastData[]; loading: boolean }>()

const sortMethod = (a: any, b: any, sortByPropName: string) => {
  return Number(a[sortByPropName]) > Number(b[sortByPropName]) ? 1 : -1
}

const rowClassName = (item: any) => {
  if (item.row.is_peak_hour) {
    return 'el-table__row--peak-hour'
  }
  return ''
}

const cellClassName = (row: any) => {
  if (row.is_peak_hour) {
    return
  }

  if (row.actual_load_mwh > row.expected_load_mwh) {
    return 'load-forecast-table-cell--red'
  } else if (row.actual_load_mwh < row.expected_load_mwh) {
    return 'load-forecast-table-cell--green'
  }
}
</script>

<template>
  <div class="load-forecast-table">
    <el-card v-loading="props.loading" class="load-forecast-table__card">
      <div class="load-forecast-table__content">
        <el-table :data="props.data" style="width: 100%" max-height="450" :row-class-name="rowClassName">
          <el-table-column sortable prop="iso" label="ISO" width="120" />
          <el-table-column sortable prop="load_resource_zone" label="Zone" width="120" />
          <el-table-column sortable prop="timestamp" label="Day">
            <template #default="{ row }">
              {{ formatDateUSCentralShort.format(new Date(row.timestamp)) }}
            </template>
          </el-table-column>
          <el-table-column sortable prop="timestamp" label="Hour Ending">
            <template #default="{ row }">
              {{ new Date(formatDateUSCentral.format(new Date(row.timestamp))).getHours() + 1 }}:00</template
            >
          </el-table-column>
          <el-table-column sortable prop="issue_date" label="Forecast Issued" />
          <el-table-column
            sortable
            :sort-method="(a: any, b: any) => sortMethod(a, b, 'expected_load_mwh')"
            prop="expected_load_mwh"
            label="Expected Load"
          >
            <template #default="{ row }">
              <span> {{ formatMegawatts(row.expected_load_mwh) }} MWh </span>
            </template>
          </el-table-column>
          <el-table-column
            :sort-method="(a: any, b: any) => sortMethod(a, b, 'actual_load_mwh')"
            sortable
            prop="actual_load_mwh"
            label="Actual Load"
          >
            <template #default="{ row }">
              <span :class="cellClassName(row)">
                {{ row.actual_load_mwh !== '0.00' ? `${formatMegawatts(row.actual_load_mwh)}  MWh` : '' }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
  </div>
</template>

<style lang="scss" scoped>
.load-forecast {
  &-table {
    &-cell {
      &--red {
        color: red;
      }

      &--green {
        color: var(--text-badge-emerald);
      }
    }
  }
}

:deep(.el-table__row--peak-hour) {
  background-color: rgb(255 0 0 / 10%) !important;

  td {
    background-color: rgb(255 0 0 / 10%) !important;
  }
}
</style>
